import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TeamsService } from '../../services/teams/teams.service';
import { CanDeactivateGuard } from '../../guards/can-deactivate/can-deactivate.guard'
import { Observable, Subscription } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../components/modal/confirmdialog/confirmdialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SessionService } from '../../services/session/session.service';
import { ManagedFeatureFlagsService } from '../../services/managed-feature-flags/managed-feature-flags.service';
import { TeamCompanyUsersService } from '../../services/teamcompanyusers/teamcompanyusers.service';
import { UsersService } from '../../services/users/users.service';
import { PayrollAccessService } from '../../services/payroll-access/payroll-access.service';
import { TeamManagerWorkspaceService } from '../../services/team-manager-workspace/team-manager-workspace.service';
import {
  TEAM_ADMIN_MANAGE_WELLSPACE_PRODUCTS_FLAG,
} from '../../services/managed-feature-flags/managed-feature-flag-keys';

interface ManageWellspaceLink {
  title: string;
  link: string;
  superAdminOnly?: boolean;
  productsTab?: boolean;
}


@Component({
    selector: 'app-managewellspace',
    templateUrl: './managewellspace.component.html',
    styleUrls: ['./managewellspace.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManageWellspaceComponent implements OnInit, CanDeactivateGuard{

  @ViewChild('tabs') tabs: any;

  hasUnsavedChanges: boolean = false;

  private readonly baseLinks: ManageWellspaceLink[] = [
    { title: 'Overview', link: '/managewellspace/overview' },
    { title: 'Members', link: '/managewellspace/agency-members' },
    { title: 'Products', link: '/managewellspace/products', productsTab: true },
    { title: 'Benefits', link: '/managewellspace/categories' },
    { title: 'Plan', link: '/managewellspace/plan' },
    { title: 'Rewards history', link: '/managewellspace/program' },
  ];
  public links: ManageWellspaceLink[] = [];
  public activeLink: any;
  public teamId = 'current';
  public selectedWellspaceTeamId = '';
  public companyId = '';
  isApproved = false;
  isSuperAdmin = false;
  isExactSuperAdmin = false;
  public canOpenNewTeamsExperience = false;
  public canShowProductsTab = false;
  public availableWellspaces: any[] = [];
  public selectedWellspaceId = '';
  private productTabAccessChecked = false;
  public selectedWellspace: any;
  private selectedCompany: any;
  private selectedCompanyLoaded = false;
  private nativeAccessFlagEnabled = false;
  private selectedCompanyRequestVersion = 0;
  private meWaitSubscription?: Subscription;

  constructor(
    private router: Router,
    private teamsService: TeamsService,
    private dialog: MatDialog,
    private sessionService: SessionService,
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private teamCompanyUsersService: TeamCompanyUsersService,
    private usersService: UsersService,
    private payrollAccessService: PayrollAccessService,
    public teamManagerWorkspace: TeamManagerWorkspaceService,
  ) { }

  ngOnInit(): void {
    this.isSuperAdmin = this.sessionService.isSuperAdmin();
    this.isExactSuperAdmin = this.sessionService.isExactSuperAdmin();
    this.canShowProductsTab = this.isSuperAdmin;
    void this.refreshNewTeamsExperienceAccess();

    this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
      if (wellspace) {
        this.applySelectedWellspace(wellspace);
      } else if (wellspace === null) {
        this.clearSelectedWellspace();
      }
    });
    const parsedUrl = this.router.parseUrl(this.router.url);
    const urlSegments = parsedUrl.root.children.primary.segments;
    if (urlSegments.length > 2) {
      this.teamId = urlSegments[2].toString();
      if (this.teamId !== 'current') {
        this.teamsService.getTeamInfo(this.teamId).then(team => {
          this.teamsService.setWellspace(team);
        });
        void this.loadSelectedCompany(this.teamId);
      }
    }

    this.hydrateSelectedWellspaceFromCurrentContext();
    if (this.isSuperAdmin) {
      this.productTabAccessChecked = true;
    }
    this.updateVisibleLinks();
    this.syncActiveLinkWithRoute(parsedUrl.toString());
    this.loadAvailableWellspaces();
    void this.refreshProductsTabAccess();
  }

  private hydrateSelectedWellspaceFromCurrentContext(): void {
    if (this.teamId !== 'current') {
      return;
    }

    const currentWellspace = this.teamsService.wellspaceChangedObservable.value;
    if (currentWellspace) {
      this.applySelectedWellspace(currentWellspace);
    } else {
      void this.teamsService.ensureValidatedSelectedWellspace();
    }
  }

  private clearSelectedWellspace(): void {
    this.selectedCompanyRequestVersion++;
    this.selectedWellspace = undefined;
    this.selectedCompany = undefined;
    this.selectedCompanyLoaded = false;
    this.selectedWellspaceId = '';
    this.selectedWellspaceTeamId = '';
    this.companyId = '';
    this.isApproved = false;
    this.productTabAccessChecked = this.isSuperAdmin;
    this.canShowProductsTab = this.isSuperAdmin;
    this.updateNewTeamsExperienceAvailability();
    this.updateVisibleLinks();
  }

  private applySelectedWellspace(wellspace: any): void {
    this.selectedWellspace = wellspace;
    this.isApproved = wellspace?.approved === 1;
    this.selectedWellspaceTeamId = wellspace?.id ?? '';
    this.selectedWellspaceId = wellspace?.id ?? '';
    this.companyId = wellspace?.companyId ?? '';
    this.selectedCompany = undefined;
    this.selectedCompanyLoaded = false;
    if (wellspace?.id) {
      void this.loadSelectedCompany(wellspace.id);
    }
    this.updateNewTeamsExperienceAvailability();
    void this.refreshProductsTabAccess();
  }

  private async loadSelectedCompany(teamId: string): Promise<void> {
    const normalizedTeamId = `${teamId || ''}`.trim();
    if (!normalizedTeamId) {
      return;
    }

    const requestVersion = ++this.selectedCompanyRequestVersion;
    this.selectedCompany = undefined;
    this.selectedCompanyLoaded = false;
    this.updateNewTeamsExperienceAvailability();
    try {
      const company = await this.teamsService.getCompanyForTeam(normalizedTeamId);
      if (
        requestVersion !== this.selectedCompanyRequestVersion
        || this.activeProductsTeamId !== normalizedTeamId
      ) {
        return;
      }

      this.selectedCompany = company;
      this.selectedCompanyLoaded = true;
      this.companyId = `${company?.id || ''}`.trim();
    } catch (error) {
      if (requestVersion !== this.selectedCompanyRequestVersion) {
        return;
      }
      this.selectedCompany = undefined;
      this.selectedCompanyLoaded = false;
      this.companyId = '';
      console.error('Failed to resolve the selected Wellspace company', error);
    }

    this.updateNewTeamsExperienceAvailability();
    void this.refreshProductsTabAccess();
  }

  private async refreshNewTeamsExperienceAccess(): Promise<void> {
    try {
      const access = await this.payrollAccessService.refresh();
      this.nativeAccessFlagEnabled = access.canShowPayrollAdminNav;
    } catch (error) {
      this.nativeAccessFlagEnabled = false;
      console.error('Failed to evaluate the new Teams experience access', error);
    }
    this.updateNewTeamsExperienceAvailability();
  }

  private updateNewTeamsExperienceAvailability(): void {
    const teamId = `${this.activeProductsTeamId || ''}`.trim();
    this.canOpenNewTeamsExperience = this.isExactSuperAdmin
      && this.nativeAccessFlagEnabled
      && !!teamId
      && this.selectedCompanyLoaded;
  }

  private loadAvailableWellspaces(): void {
    const userId = this.sessionService.getUserId();
    if (!userId) {
      this.availableWellspaces = [];
      return;
    }

    this.teamsService.getUserTeamsInfo(userId).then((teams: any[]) => {
      this.availableWellspaces = teams || [];
      this.availableWellspaces.forEach(wellspace => {
        wellspace.role = wellspace.ownerUserId === userId ? '' : 'admin';
      });
    }).catch(error => {
      this.availableWellspaces = [];
      console.error('Failed to load Manage Wellspace switcher teams', error);
    });
  }

  private buildLink(link: ManageWellspaceLink): ManageWellspaceLink {
    return {
      ...link,
      link: `${link.link}/${this.teamId || 'current'}`,
    };
  }

  private updateVisibleLinks(): void {
    this.links = this.baseLinks
      .filter((link) => !link.productsTab || this.canShowProductsTab)
      .filter((link) => this.isSuperAdmin || !link.superAdminOnly)
      .map((link) => this.buildLink(link));
  }

  private getManageWellspaceRouteSection(route: string): string {
    const parsedRoute = this.router.parseUrl(route);
    const segments = parsedRoute.root.children.primary?.segments || [];
    const manageWellspaceIndex = segments.findIndex((segment) => segment.path === 'managewellspace');
    return manageWellspaceIndex >= 0
      ? segments[manageWellspaceIndex + 1]?.path || ''
      : '';
  }

  private syncActiveLinkWithRoute(
    route = this.router.parseUrl(this.router.url).toString(),
    navigateUnavailableProducts = false,
  ): void {
    const routeSection = this.getManageWellspaceRouteSection(route);
    this.activeLink = this.links.find((link) => (
      this.getManageWellspaceRouteSection(link.link) === routeSection
    ));

    if (!this.activeLink && routeSection === 'products' && !this.productTabAccessChecked) {
      return;
    }

    if (
      !this.activeLink
      && routeSection === 'products'
      && this.productTabAccessChecked
      && navigateUnavailableProducts
      && this.links.length > 0
    ) {
      this.activeLink = this.links[0];
      this.router.navigate([this.activeLink.link]);
    }
  }

  private async refreshProductsTabAccess(): Promise<void> {
    if (this.isSuperAdmin) {
      this.canShowProductsTab = true;
      this.productTabAccessChecked = true;
      this.updateVisibleLinks();
      this.syncActiveLinkWithRoute();
      return;
    }

    const teamId = this.activeProductsTeamId;
    const companyId = this.companyId;
    if (!teamId || !companyId) {
      if (teamId && !companyId) {
        this.productTabAccessChecked = false;
        this.updateVisibleLinks();
        this.syncActiveLinkWithRoute();
        return;
      }
      this.canShowProductsTab = false;
      this.productTabAccessChecked = true;
      this.updateVisibleLinks();
      this.syncActiveLinkWithRoute(undefined, true);
      return;
    }

    try {
      const rolesResponse = await this.teamCompanyUsersService.getManagers();
      const roles = rolesResponse?.plain?.() || rolesResponse || [];
      const hasScopedManagerRole = Array.isArray(roles) && roles.some((role: any) => {
        return role?.companyId === companyId
          && role?.role === 'manager'
          && `${role?.type || ''}`.trim().toLowerCase() === 'employer';
      });
      const hasScopedTeamAdminRole = this.hasSelectedWellspaceAdminAccess(teamId);

      if (!hasScopedManagerRole && !hasScopedTeamAdminRole) {
        this.canShowProductsTab = false;
      } else {
        const me = this.usersService.me() || await this.waitForMe();
        const partnerId = `${me?.user?.partnerId || me?.brand?.partnerId || ''}`.trim() || undefined;
        this.canShowProductsTab = await this.managedFeatureFlagsService.isEnabled(
          TEAM_ADMIN_MANAGE_WELLSPACE_PRODUCTS_FLAG,
          'team-admin',
          {
            userId: this.sessionService.getUserId(),
            teamId,
            partnerId,
          },
        );
      }
    } catch (error) {
      this.canShowProductsTab = false;
      console.error('Failed to evaluate Manage Wellspace products access', error);
    } finally {
      this.productTabAccessChecked = true;
      this.updateVisibleLinks();
      this.syncActiveLinkWithRoute(undefined, true);
    }
  }

  private hasSelectedWellspaceAdminAccess(teamId: string): boolean {
    if (!teamId || this.selectedWellspace?.id !== teamId) {
      return false;
    }

    const userId = this.sessionService.getUserId();
    return this.selectedWellspace?.ownerUserId === userId
      || this.selectedWellspace?.role === 'admin';
  }

  private waitForMe(timeoutMs = 500): Promise<any> {
    const existing = this.usersService.me();
    if (existing) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      let settled = false;
      const finish = (value: any) => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        this.meWaitSubscription?.unsubscribe();
        this.meWaitSubscription = undefined;
        resolve(value || null);
      };

      const timer = setTimeout(() => finish(this.usersService.me()), timeoutMs);
      this.meWaitSubscription = this.usersService.meSubscribe((me: any) => {
        if (me) {
          finish(me);
        }
      });
    });
  }

  public get activeProductsTeamId(): string | undefined {
    return this.teamId !== 'current'
      ? this.teamId
      : this.selectedWellspaceTeamId || undefined;
  }

  public get selectedWellspaceName(): string {
    return this.selectedWellspace?.name || 'Select a Wellspace';
  }

  public get selectedWellspaceAccessLabel(): string {
    if (!this.selectedWellspace) {
      return 'Choose a team to manage.';
    }

    return this.selectedWellspace.role ? 'Admin access' : 'Owner access';
  }

  public switchManagedWellspace(teamId: string): void {
    const team = this.availableWellspaces.find(item => item.id === teamId);
    if (!team) {
      return;
    }

    void this.teamsService.setWellspace(team);
    const routeSection = this.getManageWellspaceRouteSection(this.router.url) || 'overview';
    const nextRoute = `/managewellspace/${routeSection}/current`;
    if (this.router.url.split('?')[0] !== nextRoute) {
      this.router.navigate([nextRoute], { queryParamsHandling: 'preserve' });
    }
  }

  public get showNativeTeamSettings(): boolean {
    return this.teamManagerWorkspace.useNativeNavigation
      || (this.isExactSuperAdmin && this.teamManagerWorkspace.state.enabled
        && this.router.parseUrl(this.router.url).queryParams?.source === 'my-team');
  }

  public async openNewTeamsExperience(): Promise<void> {
    const teamId = `${this.activeProductsTeamId || ''}`.trim();
    const companyId = `${this.companyId || ''}`.trim();
    if (!this.canOpenNewTeamsExperience || !teamId) {
      return;
    }

    if (!companyId || `${this.selectedCompany?.type || ''}`.trim().toLowerCase() !== 'employer') {
      const userId = this.sessionService.getUserId();
      const team = await this.teamsService.setWellspace({ id: teamId });
      if (team?.id !== teamId || this.activeProductsTeamId !== teamId
        || this.selectedWellspaceTeamId !== teamId || this.sessionService.getUserId() !== userId
        || !this.sessionService.isExactSuperAdmin() || !this.nativeAccessFlagEnabled) return;
      void this.router.navigate(['/my-team'], {
        queryParams: { workspace: null, companyId: null, teamId: null, view: null, source: 'managewellspace' },
        queryParamsHandling: 'merge',
      });
      return;
    }

    void this.router.navigate(['/payrolladmin/teams'], {
      queryParams: {
        workspace: 'company',
        companyId,
        teamId,
        view: 'details',
        source: 'managewellspace',
      },
      queryParamsHandling: 'merge',
    });
  }

  public setHasUnsavedChanges(hasUnsavedChanges: boolean): void {
    hasUnsavedChanges ? this.hasUnsavedChanges = false : this.hasUnsavedChanges = true;
  }

  public canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.hasUnsavedChanges) {
      return new Promise((resolve) => {
        const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
          data: new ConfirmDialogModel('You have unsaved changes.', 'Do you really want to leave?')
        });
       
        dialogRef.afterClosed().subscribe(result => {
          resolve(result);
        });
      });
    }
    return true;
  }

  public onLinkClick(link: any): void {
    if (this.hasUnsavedChanges) {
      const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
        data: new ConfirmDialogModel('You have unsaved changes.', 'Do you really want to leave?')
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.activeLink = link;
          this.hasUnsavedChanges = false;
          this.router.navigate([link.link], { queryParamsHandling: 'preserve' });
        }
      });
    } else {
      this.activeLink = link;
      this.router.navigate([link.link], { queryParamsHandling: 'preserve' });
    }
  }
}
